import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import {
  queryClient,
  teachurKeys,
  useCancelScheduledCallMutation,
  useStudentCancelScheduledCallMutation,
} from '@teachur/http-client';

import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { Textarea } from '@new/common/ui/Textarea/Textarea';
import { getErrorMessage } from '@new/lib';

interface CancelScheduledCallModalProps {
  callId: string;
  courseId?: number;
  isOpen: boolean;
  onClose: () => void;
  mode: 'student' | 'instructor';
}

export const CancelScheduledCallModal: FC<CancelScheduledCallModalProps> = ({
  callId,
  courseId,
  isOpen,
  onClose,
  mode,
}) => {
  const [reason, setReason] = useState('');

  const isStudent = mode === 'student';

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: teachurKeys.scheduledCalls.all,
    });

    await queryClient.invalidateQueries({
      queryKey: teachurKeys.scheduledCalls.today(),
    });

    if (courseId) {
      await queryClient.invalidateQueries({
        queryKey: teachurKeys.scheduledCalls.list({
          courseId,
        }),
      });

      await queryClient.invalidateQueries({
        queryKey: teachurKeys.courses.completion(courseId),
      });

      await queryClient.invalidateQueries({
        queryKey: teachurKeys.courseHearings.all,
      });
    }

    setReason('');
    onClose();
  };

  const handleError = (error: unknown) => {
    toast.error(getErrorMessage(error, 'Failed to cancel course hearing'));
  };

  const studentCancelMutation = useStudentCancelScheduledCallMutation(courseId, {
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const instructorCancelMutation = useCancelScheduledCallMutation(courseId, {
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const isPending = studentCancelMutation.isPending || instructorCancelMutation.isPending;

  const handleClose = () => {
    if (isPending) return;

    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    const trimmedReason = reason.trim();

    if (!trimmedReason || !callId) return;

    const variables = {
      callId,
      reason: trimmedReason,
    };

    if (isStudent) {
      studentCancelMutation.mutate(variables);
      return;
    }

    instructorCancelMutation.mutate(variables);
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title="Cancel course hearing?"
      description="Are you sure you want to cancel this course hearing? Please tell us why."
      confirmText="Yes"
      cancelText="No"
      isLoading={isPending}
      confirmDisabled={!reason.trim()}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Textarea
        value={reason}
        onChange={setReason}
        placeholder="Reason for cancelling..."
        rows={3}
      />
    </ConfirmationModal>
  );
};
